import { existsSync, mkdirSync, readFileSync } from "node:fs";
import { join, relative } from "node:path";
import chalk from "chalk";
import type { CaiConfig } from "./types.js";
import { findScaffoldFiles } from "./drift/index.js";
import { writeIfChanged } from "./utils/fs.js";
import { mergeWithMarkers } from "./utils/merge.js";

/**
 * Maps .cai/ context files to path-scoped .claude/rules/ files.
 * Each rule loads ONLY when Claude touches matching source paths.
 */
interface RuleMapping {
  /** Source .cai/ file */
  source: string;
  /** Target .claude/rules/ filename */
  target: string;
  /** Glob patterns — rule loads only when Claude works in these paths */
  paths: string[];
  /** Human-readable description — helps Claude decide rule relevance */
  description: string;
  /** Whether this rule should always load (no path restriction) */
  global?: boolean;
}

/** Default rule mappings from scaffold context to path-scoped rules */
function getRuleMappings(projectRoot: string): RuleMapping[] {
  const mappings: RuleMapping[] = [
    {
      source: "context/setup.md",
      target: "setup.md",
      description: "Dev environment setup, commands, prerequisites, and common issues.",
      paths: ["docker-compose*", "Dockerfile*", ".env*", "*.config.*", "scripts/**", "infrastructure/**", "deploy/**", ".github/**"],
    },
    {
      source: "context/architecture.md",
      target: "architecture.md",
      description: "System architecture, component relationships, and data flow.",
      paths: ["src/**", "lib/**", "app/**", "packages/**"],
    },
    {
      source: "context/conventions.md",
      target: "conventions.md",
      description: "Code conventions: naming, structure, patterns, and verification checklist.",
      paths: ["src/**", "lib/**", "app/**", "packages/**"],
    },
    {
      source: "context/stack.md",
      target: "stack.md",
      description: "Technology stack, library choices, and version constraints.",
      paths: ["package.json", "tsconfig.json", "*.config.*"],
    },
    {
      source: "context/decisions.md",
      target: "decisions.md",
      description: "Key architectural decisions with reasoning and alternatives considered.",
      paths: ["src/**", "lib/**", "app/**", "packages/**", "docs/**", "*.md"],
    },
  ];

  // Detect project-specific source dirs to refine path scoping
  const dirs = ["src", "lib", "app", "packages", "apps", "internal", "server", "client"];
  const presentDirs = dirs.filter((d) => existsSync(join(projectRoot, d)));

  if (presentDirs.length > 0) {
    const globbed = presentDirs.map((d) => `${d}/**`);
    // Refine architecture and conventions to actual present dirs
    for (const m of mappings) {
      if (!m.global && m.paths.some((p) => p.startsWith("src/"))) {
        m.paths = globbed;
      }
    }
  }

  return mappings;
}

export interface RulesResult {
  rulesDir: string;
  written: string[];
  skipped: string[];
}

/**
 * Generate .claude/rules/ from .cai/context/ files.
 * Each rule file gets YAML frontmatter with path restrictions.
 */
export function generateRules(config: CaiConfig): RulesResult {
  const { projectRoot, scaffoldRoot } = config;
  const rulesDir = join(projectRoot, ".claude", "rules");
  mkdirSync(rulesDir, { recursive: true });

  const mappings = getRuleMappings(projectRoot);
  const written: string[] = [];
  const skipped: string[] = [];

  for (const mapping of mappings) {
    const sourcePath = join(scaffoldRoot, mapping.source);
    if (!existsSync(sourcePath)) {
      skipped.push(mapping.source);
      continue;
    }

    const content = readFileSync(sourcePath, "utf8");

    // Strip any existing frontmatter from .cai/ file
    const stripped = content.replace(/^---[\s\S]*?---\n*/, "").trim();
    if (!stripped) {
      skipped.push(mapping.source);
      continue;
    }

    // Build rule with path-scoped frontmatter.
    // Uses `globs:` instead of `paths:` — undocumented but more reliable.
    // `paths:` has bugs with YAML-reserved chars like * and { (Claude Code issues #13905, #17204).
    let ruleContent: string;
    if (mapping.global) {
      // A global rule must auto-load in every context. Without frontmatter Cursor
      // classifies it as a Manual rule and never attaches it, so emit
      // `alwaysApply: true` + `description:` instead of a bare heading.
      ruleContent = `---\ndescription: "${mapping.description}"\nalwaysApply: true\n---\n\n# ${mapping.target.replace(".md", "")} (auto-generated — edit .cai/${mapping.source})\n\n${stripped}\n`;
    } else {
      const globsYaml = mapping.paths.map((p) => `  - "${p}"`).join("\n");
      ruleContent = `---\ndescription: "${mapping.description}"\nglobs:\n${globsYaml}\n---\n\n# ${mapping.target.replace(".md", "")} (auto-generated — edit .cai/${mapping.source})\n\n${stripped}\n`;
    }

    const targetPath = join(rulesDir, mapping.target);
    const existing = existsSync(targetPath) ? readFileSync(targetPath, "utf8") : null;
    const merged = mergeWithMarkers(existing, ruleContent);
    writeIfChanged(targetPath, merged);
    written.push(mapping.target);
  }

  // Also generate rules from pattern files (if they exist)
  const patternsDir = join(scaffoldRoot, "patterns");
  if (existsSync(patternsDir)) {
    const scaffoldFiles = findScaffoldFiles(projectRoot, scaffoldRoot);
    const patternFiles = scaffoldFiles.filter((f) => f.includes("/patterns/") && !f.endsWith("INDEX.md") && !f.endsWith("README.md"));

    for (const f of patternFiles) {
      const rel = relative(scaffoldRoot, f); // e.g. patterns/add-endpoint.md
      const raw = readFileSync(f, "utf8");

      // Extract globs/paths from frontmatter if present, to scope the rule.
      // Accepts both `globs:` and `paths:` in source, always emits `globs:`.
      const fmMatch = raw.match(/^---\n([\s\S]*?)\n---/);
      let globsYaml: string | null = null;
      if (fmMatch) {
        const globsLine = fmMatch[1].match(/^(?:globs|paths):\s*\n((?:\s+-[^\n]*\n?)*)/m);
        if (globsLine) {
          // Normalize to globs: regardless of source format
          globsYaml = globsLine[0].replace(/^paths:/, "globs:").trimEnd();
        }
      }

      const body = raw.replace(/^---[\s\S]*?---\n*/, "").trim();
      if (!body) continue;

      const targetName = rel.replace(/\//g, "-");
      let ruleContent: string;
      if (globsYaml) {
        ruleContent = `---\n${globsYaml}\n---\n\n# ${rel} (auto-generated — edit .cai/${rel})\n\n${body}\n`;
      } else {
        ruleContent = `# ${rel} (auto-generated — edit .cai/${rel})\n\n${body}\n`;
      }

      const targetPath = join(rulesDir, targetName);
      const existing = existsSync(targetPath) ? readFileSync(targetPath, "utf8") : null;
      const merged = mergeWithMarkers(existing, ruleContent);
      writeIfChanged(targetPath, merged);
      written.push(targetName);
    }
  }

  return { rulesDir, written, skipped };
}

export function printRulesResult(result: RulesResult): void {
  console.log();
  console.log(`  ${chalk.bold("cai rules")} ${chalk.dim("· path-scoped rules for Claude Code")}`);
  console.log();

  if (result.written.length === 0) {
    console.log(chalk.dim("  No scaffold context files found to generate rules from."));
    console.log(chalk.dim("  Run cai setup first."));
    console.log();
    return;
  }

  for (const file of result.written) {
    console.log(`  ${chalk.green("✓")} .claude/rules/${chalk.cyan(file)}`);
  }
  console.log();
  console.log(chalk.dim(`  ${result.written.length} rules generated — Claude loads them only when touching matching files.`));
  console.log(chalk.dim("  These are regenerated from .cai/ on every cai sync. Edit the source, not the rules."));
  console.log();
}
